import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { isAuthConfigured } from './config';
import { rememberReturnTo } from './AuthProvider';

/**
 * Gate for authenticated routes. Remembers where the user was going so sign-in
 * returns them there instead of dumping everyone on the overview.
 *
 * ⚠️ **Waits out MSAL's startup before deciding.** `useIsAuthenticated()`
 * reports `false` while `inProgress` is still `Startup`, because the account
 * list has not been read yet — "no accounts yet" and "not signed in" are
 * indistinguishable at that moment. Redirecting on it sends a signed-in
 * person to the sign-in screen on every cold load, which is the redirect loop
 * `AuthProvider` exists to prevent. `AuthProvider` does prevent it in the real
 * app by not rendering until `handleRedirectPromise` resolves — but a guard
 * that is only correct because something upstream is careful is exactly the
 * kind of thing OD-18's reasoning says not to rely on. So it is checked here
 * too, where the decision is actually made.
 *
 * When auth is not configured (a local checkout with no staff app
 * registration) the gate is inert — otherwise the console would be entirely
 * unreachable for anyone developing it.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const isAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();
  const location = useLocation();

  if (!isAuthConfigured || isAuthenticated) {
    return <>{children}</>;
  }

  // Not yet a decision — MSAL has not finished looking.
  if (inProgress === InteractionStatus.Startup || inProgress === InteractionStatus.HandleRedirect) {
    return null;
  }

  rememberReturnTo(location.pathname + location.search);
  return <Navigate to="/signin" replace />;
}
